import { useState, useMemo, useCallback } from 'react';
import { useScore } from '../../../context/ScoreContext';
import { useRatings } from '../../../hooks/useRatings';

const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";
const FONT_DECO = "'Cinzel', serif";
const FONT_HUD  = "'Orbitron', monospace";

interface Props { onClose: () => void; }

// ── Read-only Stars ───────────────────────────────────────────────────────────
function StarReadOnly({ score, color = '#d4a843', size = 'md' }: { score: number; color?: string; size?: 'sm' | 'md' }) {
  const sz = size === 'sm' ? '1.1rem' : '1.4rem';
  const btnSz = size === 'sm' ? 'w-7 h-7' : 'w-8 h-8';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <div key={s} className={`${btnSz} flex items-center justify-center shrink-0`}>
          <i className={s <= score ? 'ri-star-fill' : 'ri-star-line'}
            style={{
              fontSize: sz,
              color: s <= score ? color : '#1e2a3a',
              filter: s <= score ? `drop-shadow(0 0 4px ${color}70)` : 'none',
            }} />
        </div>
      ))}
    </div>
  );
}

// ── Star Row (interactive) — touch-optimised ──────────────────────────────────
function StarRow({ score, onChange, color = '#d4a843' }: { score: number; onChange: (s: number) => void; color?: string }) {
  const labels = ['', '很一般', '普通', '不錯', '很棒', '太厲害！'];
  return (
    <div>
      <div className="flex items-center gap-1 py-0.5">
        {[1, 2, 3, 4, 5].map(s => {
          const filled = s <= score;
          return (
            <button
              key={s}
              // onTouchStart fires instantly without 300ms delay; preventDefault stops ghost click
              onTouchStart={e => { e.preventDefault(); onChange(s); }}
              onClick={() => onChange(s)}
              className="w-9 h-9 flex items-center justify-center cursor-pointer shrink-0"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            >
              <i
                className={filled ? 'ri-star-fill' : 'ri-star-line'}
                style={{
                  fontSize: '1.35rem',
                  color: filled ? color : '#2a3a5a',
                  filter: filled ? `drop-shadow(0 0 5px ${color}80)` : 'none',
                  display: 'block',
                  pointerEvents: 'none',
                }}
              />
            </button>
          );
        })}
      </div>
      {score > 0 && (
        <p className="text-[10px] mt-0.5" style={{ color: `${color}70`, fontFamily: "'Rajdhani','Noto Sans TC',system-ui,sans-serif" }}>
          {labels[score]}
        </p>
      )}
    </div>
  );
}

// ── Locked View ───────────────────────────────────────────────────────────────
function LockedView({
  isTeamMode,
  myTeam,
  myRatings,
  enemyMembers,
  teamColor,
  teamRgb,
  onClose,
}: {
  isTeamMode: boolean;
  myTeam: 'black' | 'white';
  myRatings: Map<string, { score: number }>;
  enemyMembers: ReturnType<typeof useScore>['members'];
  teamColor: string;
  teamRgb: string;
  onClose: () => void;
}) {
  const enemyTeamId = myTeam === 'black' ? 'team-white' : 'team-black';
  const enemyTeamLabel = myTeam === 'black' ? '白隊' : '黑隊';
  const enemyTeamColor = myTeam === 'black' ? '#e0e0e0' : '#d4a843';
  const enemyTeamRgb   = myTeam === 'black' ? '224,224,224' : '212,168,67';

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto min-h-0 px-5 py-5 flex flex-col gap-5">

        {/* Locked header */}
        <div className="flex flex-col items-center gap-3 py-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{
                background: `radial-gradient(circle, rgba(${teamRgb},0.2), rgba(${teamRgb},0.05))`,
                border: `2px solid rgba(${teamRgb},0.5)`,
                boxShadow: `0 0 24px rgba(${teamRgb},0.3)`,
              }}>
              <i className="ri-lock-2-fill text-2xl" style={{ color: teamColor }} />
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
              style={{ background: '#16a34a', border: '2px solid #040c1e' }}>
              <i className="ri-check-line text-xs text-white" />
            </div>
          </div>

          <div className="text-center">
            <p className="text-white font-black text-base" style={{ fontFamily: FONT_BODY }}>
              評分已提交！
            </p>
            <p className="text-xs mt-1" style={{ color: `rgba(${teamRgb},0.5)`, fontFamily: FONT_BODY }}>
              你的評分已同步至裁判，無法再修改
            </p>
          </div>
        </div>

        {/* Submitted ratings — read only */}
        <div className="space-y-3">
          <p className="text-[10px] font-black tracking-[0.35em] uppercase"
            style={{ color: `rgba(${teamRgb},0.45)`, fontFamily: FONT_DECO }}>
            你的評分紀錄
          </p>

          {isTeamMode ? (
            // Team mode: show one team card
            (() => {
              const s = myRatings.get(enemyTeamId)?.score ?? 0;
              return (
                <div className="rounded-2xl px-4 py-4 flex items-center gap-4"
                  style={{
                    background: `linear-gradient(135deg, rgba(${enemyTeamRgb},0.10), rgba(${enemyTeamRgb},0.03))`,
                    border: `1.5px solid rgba(${enemyTeamRgb},0.3)`,
                  }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: `rgba(${enemyTeamRgb},0.15)`, border: `1.5px solid rgba(${enemyTeamRgb},0.4)` }}>
                    <i className={myTeam === 'black' ? 'ri-sword-fill' : 'ri-shield-fill'}
                      style={{ color: enemyTeamColor, fontSize: '1rem' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black" style={{ color: enemyTeamColor, fontFamily: FONT_BODY }}>{enemyTeamLabel}</p>
                    <StarReadOnly score={s} color={enemyTeamColor} />
                  </div>
                  <span className="text-xl font-black tabular-nums shrink-0"
                    style={{ color: enemyTeamColor, fontFamily: FONT_HUD }}>{s}</span>
                </div>
              );
            })()
          ) : (
            // Individual mode: show all rated members
            enemyMembers.map(m => {
              const s = myRatings.get(m.id)?.score ?? 0;
              const color = m.team === 'black' ? '#d4a843' : '#e0e0e0';
              const rgb   = m.team === 'black' ? '212,168,67' : '224,224,224';
              return (
                <div key={m.id}
                  className="rounded-2xl px-4 py-3 flex items-center gap-3"
                  style={{
                    background: `linear-gradient(135deg, rgba(${rgb},0.08), rgba(${rgb},0.02))`,
                    border: `1.5px solid rgba(${rgb},0.2)`,
                  }}>
                  {m.avatarUrl ? (
                    <img src={m.avatarUrl} alt={m.name}
                      className="w-9 h-9 rounded-full object-cover object-top shrink-0"
                      style={{ border: `2px solid rgba(${rgb},0.4)` }} />
                  ) : (
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black shrink-0"
                      style={{ background: `rgba(${rgb},0.15)`, color, border: `2px solid rgba(${rgb},0.3)` }}>
                      {m.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black truncate" style={{ color: 'white', fontFamily: FONT_BODY }}>{m.name}</p>
                    <StarReadOnly score={s} color={color} size="sm" />
                  </div>
                  <span className="text-base font-black tabular-nums shrink-0"
                    style={{ color, fontFamily: FONT_HUD }}>{s}★</span>
                </div>
              );
            })
          )}
        </div>

        {/* Info note */}
        <div className="rounded-xl px-4 py-3 flex items-start gap-2.5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <i className="ri-information-line text-base shrink-0 mt-0.5" style={{ color: '#2a3a5a' }} />
          <p className="text-[11px] leading-relaxed" style={{ color: '#2a3a5a', fontFamily: FONT_BODY }}>
            評分提交後即鎖定，確保結果公正。如有問題請聯絡裁判。
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-4 border-t shrink-0" style={{ borderColor: `rgba(${teamRgb},0.12)` }}>
        <button
          onClick={onClose}
          className="w-full py-3.5 rounded-xl font-black text-sm cursor-pointer whitespace-nowrap"
          style={{
            background: `rgba(${teamRgb},0.1)`,
            color: teamColor,
            border: `1.5px solid rgba(${teamRgb},0.3)`,
            fontFamily: FONT_DECO,
          }}>
          <span className="flex items-center justify-center gap-2">
            <i className="ri-close-line" />
            關閉
          </span>
        </button>
      </div>
    </div>
  );
}

// ── Team Mode ─────────────────────────────────────────────────────────────────
function TeamVoting({
  myTeam,
  myRatings,
  onSubmit,
}: {
  myTeam: 'black' | 'white';
  myRatings: Map<string, { score: number }>;
  onSubmit: (items: { targetId: string; targetName: string; targetTeam: 'black' | 'white'; score: number; comment: string }[]) => void;
}) {
  const enemyTeam = myTeam === 'black'
    ? { id: 'team-white', name: '白隊', team: 'white' as const, color: '#e0e0e0', rgb: '224,224,224', icon: 'ri-sword-fill' }
    : { id: 'team-black', name: '黑隊', team: 'black' as const, color: '#d4a843', rgb: '212,168,67', icon: 'ri-shield-fill' };

  const [score, setScore] = useState<number>(myRatings.get(enemyTeam.id)?.score ?? 0);
  const labels = ['', '很一般', '普通', '不錯', '很棒', '太厲害！'];

  const myTeamLabel = myTeam === 'black' ? '黑隊' : '白隊';
  const myTeamColor = myTeam === 'black' ? '#d4a843' : '#e0e0e0';
  const myTeamRgb   = myTeam === 'black' ? '212,168,67' : '224,224,224';

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto min-h-0 px-5 py-3 flex flex-col items-center gap-4">

        {/* VS banner */}
        <div className="w-full flex items-center justify-center gap-4">
          <div className="flex flex-col items-center gap-1">
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: `rgba(${myTeamRgb},0.15)`, border: `2px solid rgba(${myTeamRgb},0.45)`, color: myTeamColor, fontSize: '1.2rem' }}>
              <i className={myTeam === 'black' ? 'ri-shield-fill' : 'ri-sword-fill'} />
            </div>
            <p className="text-[10px] font-black" style={{ color: myTeamColor, fontFamily: "'Rajdhani','Noto Sans TC',system-ui,sans-serif" }}>{myTeamLabel}</p>
            <p className="text-[9px]" style={{ color: `rgba(${myTeamRgb},0.4)`, fontFamily: "'Rajdhani','Noto Sans TC',system-ui,sans-serif" }}>我方</p>
          </div>
          <span className="text-xl font-black" style={{ color: '#2a3a5a', fontFamily: "'Orbitron',monospace" }}>VS</span>
          <div className="flex flex-col items-center gap-1">
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: `rgba(${enemyTeam.rgb},0.15)`, border: `2px solid rgba(${enemyTeam.rgb},0.45)`, color: enemyTeam.color, fontSize: '1.2rem' }}>
              <i className={enemyTeam.icon} />
            </div>
            <p className="text-[10px] font-black" style={{ color: enemyTeam.color, fontFamily: "'Rajdhani','Noto Sans TC',system-ui,sans-serif" }}>{enemyTeam.name}</p>
            <p className="text-[9px]" style={{ color: `rgba(${enemyTeam.rgb},0.4)`, fontFamily: "'Rajdhani','Noto Sans TC',system-ui,sans-serif" }}>敵隊</p>
          </div>
        </div>

        {/* Rating card */}
        <div className="w-full rounded-2xl"
          style={{
            background: score > 0
              ? `linear-gradient(135deg, rgba(${enemyTeam.rgb},0.14), rgba(${enemyTeam.rgb},0.04))`
              : 'linear-gradient(135deg, #08100e, #06090d)',
            border: `2px solid rgba(${enemyTeam.rgb},${score > 0 ? 0.55 : 0.18})`,
            boxShadow: score > 0 ? `0 0 28px rgba(${enemyTeam.rgb},0.2)` : 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
          }}>
          <div className="px-5 py-5 flex flex-col items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full flex items-center justify-center text-3xl"
                style={{
                  background: `radial-gradient(circle at 35% 35%, rgba(${enemyTeam.rgb},0.25), rgba(${enemyTeam.rgb},0.06))`,
                  border: `2px solid rgba(${enemyTeam.rgb},${score > 0 ? 0.6 : 0.3})`,
                  boxShadow: score > 0 ? `0 0 30px rgba(${enemyTeam.rgb},0.35)` : 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                }}>
                <i className={enemyTeam.icon} style={{ color: enemyTeam.color }} />
              </div>
              {score > 0 && (
                <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-black"
                  style={{ background: enemyTeam.color, color: '#06060f', fontFamily: "'Orbitron',monospace", boxShadow: `0 0 10px ${enemyTeam.color}80` }}>
                  {score}
                </div>
              )}
            </div>

            <div className="text-center">
              <p className="text-lg font-black" style={{ color: enemyTeam.color, fontFamily: "'Cinzel',serif" }}>
                {enemyTeam.name}
              </p>
              <p className="text-xs mt-0.5" style={{ color: `rgba(${enemyTeam.rgb},0.4)`, fontFamily: "'Rajdhani','Noto Sans TC',system-ui,sans-serif" }}>
                你的對手 · 給他們打個分吧
              </p>
            </div>

            {/* Touch-optimised stars */}
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-0.5 py-1 px-1">
                {[1, 2, 3, 4, 5].map(s => {
                  const filled = s <= score;
                  return (
                    <button
                      key={s}
                      onTouchStart={e => { e.preventDefault(); setScore(s); }}
                      onClick={() => setScore(s)}
                      className="w-12 h-12 flex items-center justify-center cursor-pointer shrink-0"
                      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                    >
                      <i
                        className={filled ? 'ri-star-fill' : 'ri-star-line'}
                        style={{
                          fontSize: '1.75rem',
                          color: filled ? enemyTeam.color : '#1a2a3a',
                          filter: filled ? `drop-shadow(0 0 8px ${enemyTeam.color}80)` : 'none',
                          display: 'block',
                          pointerEvents: 'none',
                        }}
                      />
                    </button>
                  );
                })}
              </div>
              {score > 0 && (
                <p className="text-sm font-black" style={{ color: `${enemyTeam.color}90`, fontFamily: "'Rajdhani','Noto Sans TC',system-ui,sans-serif" }}>
                  {labels[score]}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-5 py-4 border-t shrink-0" style={{ borderColor: `rgba(${enemyTeam.rgb},0.12)` }}>
        <button
          onTouchStart={score > 0 ? e => e.currentTarget.style.opacity = '0.8' : undefined}
          onTouchEnd={score > 0 ? e => e.currentTarget.style.opacity = '1' : undefined}
          onClick={() => onSubmit([{ targetId: enemyTeam.id, targetName: enemyTeam.name, targetTeam: enemyTeam.team, score, comment: '' }])}
          disabled={score === 0}
          className="w-full py-3.5 rounded-xl font-black text-sm tracking-wide cursor-pointer whitespace-nowrap transition-colors disabled:opacity-35 disabled:cursor-not-allowed relative overflow-hidden"
          style={score > 0 ? {
            background: `linear-gradient(135deg, rgba(${enemyTeam.rgb},0.85), ${enemyTeam.color})`,
            color: '#06060f', boxShadow: `0 0 24px rgba(${enemyTeam.rgb},0.35)`, fontFamily: "'Cinzel',serif",
            touchAction: 'manipulation',
          } : {
            background: '#071020', color: '#2a4060', border: '1px solid #0d1e30', fontFamily: "'Cinzel',serif",
            touchAction: 'manipulation',
          }}>
          {score > 0 && (
            <div className="absolute inset-0 opacity-30 pointer-events-none"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)', animation: 'scanLine 2s linear infinite' }} />
          )}
          <span className="relative z-10 flex items-center justify-center gap-2">
            <i className="ri-send-plane-2-fill" />
            {score > 0 ? `提交 ${score} 顆星評分` : '請先打星再提交'}
          </span>
        </button>
      </div>
    </div>
  );
}

// ── Individual Mode ───────────────────────────────────────────────────────────
function IndividualVoting({
  members,
  myRatings,
  myTeam,
  onSubmit,
}: {
  members: ReturnType<typeof useScore>['members'];
  myRatings: Map<string, { score: number }>;
  myTeam: 'black' | 'white';
  onSubmit: (items: { targetId: string; targetName: string; targetTeam: 'black' | 'white'; score: number; comment: string }[]) => void;
}) {
  const enemyMembers = members.filter(m => m.team !== myTeam);
  const enemyTeam = myTeam === 'black' ? 'white' : 'black';
  const enemyColor = enemyTeam === 'black' ? '#d4a843' : '#e0e0e0';
  const enemyRgb   = enemyTeam === 'black' ? '212,168,67' : '224,224,224';
  const enemyLabel = enemyTeam === 'black' ? '黑隊' : '白隊';

  const [scores, setScores] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    enemyMembers.forEach(m => { init[m.id] = myRatings.get(m.id)?.score ?? 0; });
    return init;
  });
  const [comments, setComments] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    enemyMembers.forEach(m => { init[m.id] = ''; });
    return init;
  });

  // Memoized handler — prevents re-creation on each render, stops re-render cascade
  const handleScoreChange = useCallback((memberId: string, s: number) => {
    setScores(prev => ({ ...prev, [memberId]: s }));
  }, []);

  const allScored = enemyMembers.length > 0 && enemyMembers.every(m => (scores[m.id] ?? 0) > 0);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto min-h-0 px-5 py-3 space-y-4">
        {enemyMembers.length === 0 ? (
          <div className="text-center py-10">
            <i className="ri-group-line text-4xl block mb-3" style={{ color: '#2a3a5a' }} />
            <p className="text-[#2a3a5a] text-sm" style={{ fontFamily: "'Rajdhani','Noto Sans TC',system-ui,sans-serif" }}>對方隊伍目前沒有成員</p>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `rgba(${enemyRgb},0.15)`, border: `1px solid rgba(${enemyRgb},0.4)` }}>
                <i className={`${enemyTeam === 'black' ? 'ri-shield-fill' : 'ri-sword-fill'} text-[10px]`}
                  style={{ color: enemyColor }} />
              </div>
              <span className="text-[10px] font-black tracking-widest uppercase"
                style={{ color: enemyColor, fontFamily: "'Cinzel',serif" }}>{enemyLabel} · 敵方成員</span>
              <div className="h-px flex-1" style={{ background: `rgba(${enemyRgb},0.2)` }} />
              <span className="text-[9px]" style={{ color: `rgba(${enemyRgb},0.4)`, fontFamily: "'Rajdhani','Noto Sans TC',system-ui,sans-serif" }}>
                {enemyMembers.length} 人
              </span>
            </div>

            <div className="space-y-2">
              {enemyMembers.map((member, idx) => {
                const memberScore = scores[member.id] ?? 0;
                const hasRated = memberScore > 0;
                return (
                  <div key={member.id}
                    className="rounded-2xl"
                    style={{
                      background: hasRated
                        ? `linear-gradient(135deg, rgba(${enemyRgb},0.08), rgba(${enemyRgb},0.03))`
                        : '#060d18',
                      border: `1.5px solid rgba(${enemyRgb},${hasRated ? 0.3 : 0.1})`,
                      animation: `cardIn 0.3s ${idx * 0.06}s both`,
                    }}>
                    <div className="px-4 pt-3.5 pb-2 flex items-center gap-2.5">
                      {member.avatarUrl ? (
                        <img src={member.avatarUrl} alt={member.name}
                          className="w-9 h-9 rounded-full object-cover object-top shrink-0"
                          style={{ border: `2px solid rgba(${enemyRgb},0.45)` }} />
                      ) : (
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black shrink-0"
                          style={{ background: `rgba(${enemyRgb},0.15)`, color: enemyColor, border: `2px solid rgba(${enemyRgb},0.35)` }}>
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <p className="text-sm font-black flex-1 truncate"
                        style={{ color: hasRated ? 'white' : '#555', fontFamily: "'Rajdhani','Noto Sans TC',system-ui,sans-serif" }}>
                        {member.name}
                      </p>
                      {hasRated && (
                        <span className="text-xs font-black shrink-0"
                          style={{ color: enemyColor, fontFamily: "'Orbitron',monospace" }}>{memberScore}★</span>
                      )}
                    </div>
                    <div className="px-4 pb-2 py-1">
                      <StarRow
                        score={memberScore}
                        onChange={s => handleScoreChange(member.id, s)}
                        color={enemyColor}
                      />
                    </div>
                    <div className="px-4 pb-3">
                      <input
                        type="text"
                        value={comments[member.id] ?? ''}
                        onChange={e => setComments(prev => ({ ...prev, [member.id]: e.target.value.slice(0, 25) }))}
                        placeholder="選填評語（25字內）"
                        className="w-full rounded-xl px-3 py-1.5 text-white text-xs outline-none placeholder:text-[#1a2e48]"
                        style={{ background: '#040b16', border: '1px solid #0a1828', fontFamily: "'Rajdhani','Noto Sans TC',system-ui,sans-serif" }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {enemyMembers.length > 0 && (
        <div className="px-5 py-4 border-t shrink-0" style={{ borderColor: 'rgba(212,168,67,0.12)' }}>
          <button
            onClick={() => onSubmit(enemyMembers.map(m => ({
              targetId: m.id, targetName: m.name, targetTeam: m.team,
              score: scores[m.id] ?? 1, comment: (comments[m.id] ?? '').trim(),
            })))}
            disabled={!allScored}
            className="w-full py-3.5 rounded-xl font-black text-sm tracking-wide cursor-pointer whitespace-nowrap transition-colors disabled:opacity-35 disabled:cursor-not-allowed relative overflow-hidden"
            style={allScored ? {
              background: `linear-gradient(135deg, rgba(${enemyRgb},0.85), ${enemyColor})`,
              color: '#06060f', boxShadow: `0 0 24px rgba(${enemyRgb},0.35)`, fontFamily: "'Cinzel',serif",
              touchAction: 'manipulation',
            } : {
              background: '#071020', color: '#2a4060', border: '1px solid #0d1e30', fontFamily: "'Cinzel',serif",
              touchAction: 'manipulation',
            }}>
            {allScored && (
              <div className="absolute inset-0 opacity-30 pointer-events-none"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)', animation: 'scanLine 2s linear infinite' }} />
            )}
            <span className="relative z-10 flex items-center justify-center gap-2">
              <i className="ri-send-plane-2-fill" />提交評分
            </span>
          </button>
          {!allScored && (
            <p className="text-center text-[10px] mt-2" style={{ color: '#2a3a5a', fontFamily: "'Rajdhani','Noto Sans TC',system-ui,sans-serif" }}>
              請為敵方所有成員打星後才能提交
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function RatingModal({ onClose }: Props) {
  const { members, currentMember, myMemberId, scheduleState } = useScore();
  const { votingType, votingTitle } = scheduleState;
  const { submitRatings, getMyRatings } = useRatings();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const myRatings = useMemo(() => {
    if (!myMemberId) return new Map<string, { score: number }>();
    const rm = getMyRatings(myMemberId);
    const result = new Map<string, { score: number }>();
    rm.forEach((r, k) => result.set(k, { score: r.score }));
    return result;
  }, [myMemberId, getMyRatings]);

  const allMembers = useMemo(() => members, [members]);
  const enemyMembers = useMemo(
    () => currentMember ? members.filter(m => m.team !== currentMember.team) : [],
    [members, currentMember],
  );

  // ── Determine if already fully submitted ──────────────────────────────────
  const isTeamMode = votingType === 'team';
  const isAlreadySubmitted = useMemo(() => {
    if (!currentMember || myRatings.size === 0) return false;
    if (isTeamMode) {
      const enemyTeamId = currentMember.team === 'black' ? 'team-white' : 'team-black';
      return myRatings.has(enemyTeamId);
    }
    // Individual: check if I've submitted at least one rating for an enemy member
    // Use the rated targets from DB (myRatings) rather than current enemyMembers list
    // to avoid false "not submitted" when new members join during voting
    if (myRatings.size === 0) return false;
    const myTeam = currentMember.team;
    const ratedEnemyCount = Array.from(myRatings.values()).filter(r => r.targetTeam !== myTeam).length;
    // If I've rated at least as many enemies as currently exist, consider submitted
    // Also lock if I've rated anyone at all (prevents re-voting after new member joins)
    return ratedEnemyCount > 0 && ratedEnemyCount >= enemyMembers.length;
  }, [currentMember, myRatings, isTeamMode, enemyMembers.length]);

  const teamColor = currentMember?.team === 'black' ? '#d4a843' : '#e2e2e2';
  const teamRgb   = currentMember?.team === 'black' ? '212,168,67' : '226,226,226';

  const handleSubmit = async (items: { targetId: string; targetName: string; targetTeam: 'black' | 'white'; score: number; comment: string }[]) => {
    if (!currentMember || !myMemberId) return;
    setSubmitting(true);
    await submitRatings(myMemberId, currentMember.name, currentMember.team, items);
    setSubmitting(false);
    setDone(true);
    setTimeout(() => onClose(), 2200);
  };

  if (!currentMember) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0" style={{ background: 'rgba(2,2,10,0.92)', backdropFilter: 'blur(8px)' }} onClick={onClose} />
        <div className="relative z-10 text-center px-6">
          <i className="ri-star-line text-4xl block mb-3" style={{ color: '#2a3a5a' }} />
          <p className="text-white font-bold" style={{ fontFamily: FONT_BODY }}>請先選擇隊伍才能評分</p>
          <button onClick={onClose} className="mt-4 px-5 py-2 rounded-xl text-sm cursor-pointer whitespace-nowrap"
            style={{ background: '#0d1a2a', border: '1px solid #1e2a40', color: '#4a6a8a', fontFamily: FONT_BODY }}>關閉</button>
        </div>
      </div>
    );
  }

  // Header badge label
  const headerBadge = isAlreadySubmitted ? 'LOCKED' : isTeamMode ? 'TEAM' : 'ENEMY';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0" style={{ background: 'rgba(2,2,10,0.93)', backdropFilter: 'blur(10px)' }} onClick={onClose} />

      <div className="relative w-full max-w-md z-10 rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(160deg, #07112a 0%, #040c1e 100%)',
          border: `1.5px solid rgba(${teamRgb},0.28)`,
          boxShadow: `0 0 60px rgba(${teamRgb},0.15), 0 40px 100px rgba(0,0,0,0.9)`,
          maxHeight: '92dvh',
        }}>

        {/* Scan beam */}
        <div className="absolute top-0 left-0 right-0 h-px overflow-hidden z-20">
          <div className="h-full" style={{ background: `linear-gradient(90deg, transparent, rgba(${teamRgb},0.9), transparent)`, animation: 'scanLine 2.5s linear infinite' }} />
        </div>

        {/* Header */}
        <div className="px-5 py-4 flex items-center gap-3 border-b shrink-0"
          style={{ borderColor: `rgba(${teamRgb},0.15)`, background: `linear-gradient(90deg, rgba(${teamRgb},0.10), transparent)` }}>
          <div className="relative shrink-0">
            {!isAlreadySubmitted && (
              <div className="absolute inset-0 rounded-xl animate-ping opacity-20" style={{ background: `rgba(${teamRgb},0.5)` }} />
            )}
            <div className="relative w-10 h-10 flex items-center justify-center rounded-xl"
              style={{ background: `rgba(${teamRgb},0.15)`, border: `1.5px solid rgba(${teamRgb},0.5)` }}>
              <i
                className={isAlreadySubmitted ? 'ri-lock-2-fill' : isTeamMode ? 'ri-shield-fill' : 'ri-user-star-fill'}
                style={{ color: teamColor, fontSize: '1.1rem' }}
              />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-black text-base tracking-wide leading-tight" style={{ fontFamily: FONT_BODY }}>
              {votingTitle || (isTeamMode ? '隊伍評分' : '個人評分')}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: `rgba(${teamRgb},0.5)`, fontFamily: FONT_BODY }}>
              {isAlreadySubmitted ? '你已完成評分，結果已鎖定' : isTeamMode ? '替黑隊和白隊各打 1–5 顆星' : '替所有成員打 1–5 顆星'}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span
              className="text-[9px] px-2 py-1 rounded font-black tracking-widest uppercase"
              style={{
                background: isAlreadySubmitted ? 'rgba(22,163,74,0.15)' : `rgba(${teamRgb},0.1)`,
                color: isAlreadySubmitted ? '#4ade80' : `rgba(${teamRgb},0.6)`,
                border: `1px solid ${isAlreadySubmitted ? 'rgba(22,163,74,0.35)' : `rgba(${teamRgb},0.22)`}`,
                fontFamily: FONT_DECO,
              }}>
              {headerBadge}
            </span>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer"
              style={{ background: '#071224', border: '1px solid #0e2040', color: '#555' }}>
              <i className="ri-close-line text-base" />
            </button>
          </div>
        </div>

        {/* Body */}
        {done || submitting ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 py-14">
            {submitting ? (
              <>
                <i className="ri-loader-4-line text-4xl animate-spin" style={{ color: teamColor }} />
                <p className="text-white font-bold text-sm" style={{ fontFamily: FONT_BODY }}>提交中…</p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full flex items-center justify-center"
                  style={{ background: `radial-gradient(circle, rgba(${teamRgb},0.25), transparent)`, border: `2px solid rgba(${teamRgb},0.6)`, boxShadow: `0 0 30px rgba(${teamRgb},0.4)` }}>
                  <i className="ri-star-fill text-3xl" style={{ color: teamColor }} />
                </div>
                <div className="text-center">
                  <p className="font-black text-base text-white mb-1" style={{ fontFamily: FONT_DECO, textShadow: `0 0 20px rgba(${teamRgb},0.8)` }}>評分完成！</p>
                  <p style={{ color: `rgba(${teamRgb},0.5)`, fontSize: '12px', fontFamily: FONT_BODY }}>結果已即時同步至裁判面板</p>
                </div>
                <div className="flex items-center gap-2">
                  {[0, 100, 200, 300, 400].map(d => (
                    <div key={d} className="w-1.5 h-1.5 rounded-full"
                      style={{ background: teamColor, animation: `waveDot 0.9s ${d}ms ease-in-out infinite` }} />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : isAlreadySubmitted ? (
          // ── Locked state ──────────────────────────────────────────────────
          <LockedView
            isTeamMode={isTeamMode}
            myTeam={currentMember.team}
            myRatings={myRatings}
            enemyMembers={enemyMembers}
            teamColor={teamColor}
            teamRgb={teamRgb}
            onClose={onClose}
          />
        ) : isTeamMode ? (
          <TeamVoting myTeam={currentMember.team} myRatings={myRatings} onSubmit={handleSubmit} />
        ) : (
          <IndividualVoting members={allMembers} myRatings={myRatings} myTeam={currentMember.team} onSubmit={handleSubmit} />
        )}
      </div>

      <style>{`
        @keyframes scanLine { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
        @keyframes cardIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes waveDot { 0%,100% { opacity:.3;transform:scale(.7); } 50% { opacity:1;transform:scale(1.4); } }
      `}</style>
    </div>
  );
}
